import { useTranslation } from 'react-i18next';

import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { SlidePreview, type PreviewDevice } from './banner-preview';
import type { RailView } from './preview-model';
import { ProductCardPreview } from './product-card-preview';

export interface RailPreviewProps {
  rail: RailView;
  device: PreviewDevice;
  /** Name of the store the preview resolves in, for the count line and the empty state. */
  storeName: string;
}

/** A rail: the optional header, the resolved product cards and a "Showing N of M" line. */
export function RailPreview({ rail, device, storeName }: RailPreviewProps) {
  const { t } = useTranslation('content');
  const columns = device === 'phone' ? 'grid-cols-2' : 'grid-cols-3';
  const shown = rail.products.length;

  let summary: string | null = null;
  if (rail.selected !== null && rail.selected > 0 && !rail.loading) {
    summary =
      rail.unpublished !== null && rail.unpublished > 0
        ? t('blockPreview.rail.showingUnpublished', {
            shown,
            count: rail.selected,
            unpublished: rail.unpublished,
            store: storeName,
          })
        : t('blockPreview.rail.showing', { shown, count: rail.selected });
  } else if (rail.selected === null && rail.empty === null && !rail.loading) {
    summary = t('blockPreview.rail.showingCollection', { count: shown });
  }

  return (
    <div className="flex flex-col gap-3">
      {rail.header ? <SlidePreview slide={rail.header} device={device} /> : null}
      {rail.empty ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-4 text-center text-xs">
          {rail.empty === 'nonePublished'
            ? t('blockPreview.rail.nonePublished', { store: storeName })
            : t(`blockPreview.rail.${rail.empty}`)}
        </p>
      ) : rail.loading ? (
        <div className={cn('grid gap-3', columns)}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square" />
          ))}
        </div>
      ) : (
        <ul className={cn('grid gap-3', columns)} aria-label={t('blockPreview.rail.cards')}>
          {rail.products.map((product) => (
            <ProductCardPreview key={product.id} product={product} />
          ))}
        </ul>
      )}
      {summary ? <p className="text-muted-foreground text-xs">{summary}</p> : null}
    </div>
  );
}
